import { AlertCircle, Building2, ChevronDown, Search } from "lucide-react";
import { useAccountList } from "@/hooks/useAccounts";
import { cn } from "@/lib/cn";

export interface PickedAccount {
  id: string;
  name: string;
}

export interface AccountInlinePickerProps {
  query: string;
  onQueryChange: (q: string) => void;
  isOpen: boolean;
  onOpen: () => void;
  onPick: (a: PickedAccount) => void;
  /** Field error from the owning form (red border + text instead of the hints). */
  error?: string;
  /** id for the error text — wired to the input's aria-describedby. */
  errorId?: string;
}

/**
 * Shared inline account searcher. Used by the complaint / contract /
 * interaction create drawers for the "global / general entry" account selection.
 *
 * D-150 Q2: scope is "all" — the WIDEST requested scope; the backend clamps it
 * by resolver tier (manager → own, supervisor → team, admin → all), and ?scope
 * can only ever NARROW (AccountListService SEC-SWEEP). The old hardcoded
 * "assigned" self-narrowed to assignedUser=me, which is always empty for a
 * supervisor owning no accounts (D-150 Q2 defect). NOTE: omitting the param is
 * NOT equivalent — the endpoint defaults absent scope to 'assigned'.
 */
export function AccountInlinePicker({
  query,
  onQueryChange,
  isOpen,
  onOpen,
  onPick,
  error,
  errorId,
}: AccountInlinePickerProps) {
  const enabled = query.trim().length >= 2;
  const accountsQ = useAccountList({
    search: enabled ? query.trim() : undefined,
    scope: "all",
    limit: 8,
  });

  const list = enabled ? accountsQ.data?.items ?? [] : [];

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 focus-within:ring-2 focus-within:ring-ring",
          error && "border-destructive",
        )}
      >
        <Search className="w-4 h-4 text-muted-foreground" aria-hidden />
        <input
          autoFocus
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onOpen}
          placeholder="Знайти клієнта…"
          aria-label="Пошук клієнта"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="flex-1 bg-transparent text-sm outline-none"
        />
        <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden />
      </div>
      {error ? (
        <p id={errorId} className="flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden /> {error}
        </p>
      ) : !enabled ? (
        <p className="text-xs text-muted-foreground">
          Введіть мінімум 2 символи.
        </p>
      ) : (
        // typed but nothing picked yet — the text alone does NOT select a client.
        <p className="text-xs text-muted-foreground">Оберіть клієнта зі списку.</p>
      )}
      {enabled && isOpen && (
        <ul className="max-h-52 overflow-y-auto rounded-md border bg-background">
          {accountsQ.isPending && (
            <li className="px-3 py-2 text-xs text-muted-foreground">Завантаження…</li>
          )}
          {!accountsQ.isPending && list.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground">Нічого не знайдено</li>
          )}
          {list.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => onPick({ id: a.id, name: a.displayName })}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />
                <span className="truncate">{a.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
